import { mergeOccupants, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { groupBy, nonNullable, max } from 'lib/array/arrayUtils'
import { countTravellers } from 'checkout/lib/utils/travellerSchema/countTravellers'

export const getOccupantsFromItems = <T extends { occupancy?: App.Occupants, occupants?: App.Occupants }>(
  items: Array<T>,
  groupingKey: (item: T) => string,
  getOccupants: (item: T) => App.Occupants | undefined,
) => {
  const itemsMap = groupBy(items, groupingKey)
  return Array.from(itemsMap.values()).map(itemList => {
    const occupants = nonNullable(itemList.map(getOccupants))
    return mergeOccupants(occupants)
  })
}

/**
 * Merges multiple occupancy sources by taking the maximum count for each traveller type.
 * This ensures consistent traveller counts across quote fetching and order submission.
 *
 * Algorithm:
 * 1. For each occupancy source array, find the item with the highest total count
 * 2. Merge these representative occupancies by taking max(adults), max(children), max(infants)
 *
 * Example:
 * - Hotel room: 2 adults, 1 child (total: 3)
 * - Flight: 3 adults, 0 children (total: 3)
 * - Result: 3 adults, 1 child = 4 travellers
 *
 * @param occupancySources Array of occupancy source arrays (e.g., [flightOccupancies, accommodationOccupancies])
 * @returns Single merged occupancy using max-per-type algorithm
 */
export function mergeOccupanciesByMaxPerType(
  ...occupancySources: Array<Array<App.Occupants> | undefined>
): App.Occupants {
  const validSources = nonNullable(occupancySources).filter(source => source.length > 0)

  if (validSources.length === 0) {
    return { adults: 0, children: 0, infants: 0 }
  }

  // For each source, get the occupancy with the highest total count
  const representativeOccupancies = validSources.map(source => {
    return max(source, (occ) => sumUpOccupancies([occ])) ?? source[0]
  })

  // Merge by taking max per type
  return representativeOccupancies.reduce<App.Occupants>((merged, current) => {
    return {
      adults: Math.max(merged.adults ?? 0, current.adults ?? 0),
      children: Math.max(merged.children ?? 0, current.children ?? 0),
      infants: Math.max(merged.infants ?? 0, current.infants ?? 0),
      teenagers: Math.max(merged.teenagers ?? 0, current.teenagers ?? 0),
      childrenAge: merged.childrenAge?.length ? merged.childrenAge : current.childrenAge,
      childrenBirthDate: merged.childrenBirthDate?.length ? merged.childrenBirthDate : current.childrenBirthDate,
    }
  }, { adults: 0, children: 0, infants: 0 })
}

/**
 * Gets the total traveller count from an occupancy object.
 * Used to compare traveller counts between quote and order.
 *
 * @param occupancy The occupancy object to count
 * @returns Total number of travellers
 */
export function getTotalTravellerCount(occupancy: App.Occupants | undefined): number {
  if (!occupancy) return 0
  return sumUpOccupancies([occupancy])
}

/**
 * Converts an App.Occupants object to an array of TravellerFormSchemaOccupant.
 * Used to bridge between occupancy format and form schema format.
 *
 * @param occupants The occupancy object to convert
 * @returns Array of traveller form schema occupants
 */
export function occupantsToTravellerFormSchema(
  occupants: App.Occupants,
): Array<App.Checkout.TravellerFormSchemaOccupant> {
  return [
    ...Array(occupants.adults ?? 0).fill({ type: 'adult' as const }),
    ...Array(occupants.children ?? 0).fill({ type: 'child' as const }),
    ...Array(occupants.infants ?? 0).fill({ type: 'infant' as const }),
  ]
}

/**
 * Merges traveller arrays from insurance and flight items using max-per-type algorithm.
 * This is the form schema version of mergeOccupanciesByMaxPerType.
 *
 * @param insuranceTravellers Travellers from insurance schema item
 * @param flightTravellers Travellers from flight schema items (already merged)
 * @returns Merged traveller array using max-per-type
 */
export function mergeTravellersByMaxPerType(
  insuranceTravellers: Array<App.Checkout.TravellerFormSchemaOccupant>,
  flightTravellers: Array<App.Checkout.TravellerFormSchemaOccupant>,
): Array<App.Checkout.TravellerFormSchemaOccupant> {
  const insuranceOccupants = countTravellers(insuranceTravellers)
  const flightOccupants = countTravellers(flightTravellers)

  const merged = mergeOccupanciesByMaxPerType([insuranceOccupants], [flightOccupants])

  return occupantsToTravellerFormSchema(merged)
}
